import RequestFormLayout from 'components/layout/RequestFormLayout';
import React, { FC, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { notification } from 'antd';
import { useFormCleanup } from 'core/utils/useFormCleanup';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, CombinedStates } from 'core/types';
import useGuardingFlow from 'core/guardingRequestForm/useGuardingFlow';
import ManuallyAddVisitor from 'components/Form/ManuallyAddVisitor';
import { deleteGuardingRequest } from 'core/guardingRequestForm/guardingRequestFormApi';
import SearchVisitors from 'components/Form/SearchVisitors';
import { ClickListData } from 'components/Form/ListSearch/types';
import VisitorsAdded from 'components/Form/VisitorsAdded';
import type { AddVisitorValues } from 'components/Form/ManuallyAddVisitor/types';
import { isEmpty } from 'underscore';
import { unwrapResult } from '@reduxjs/toolkit';
import { VisitRequestFormResponsePayload } from 'core/guardingRequestForm/types';
import { updateVisitors } from 'core/guardingRequestForm/guardingRequestFormSlice';
import { GUARDING_STEPPER_LABELS, GUARDING_STEPPER_LABELS_RETAIL } from 'core/utils/stepperLabels';
import { reportUnexpectedRejection } from 'core/utils/reportUnexpectedRejection';
import type { AddedVisitor } from './types';

const GuardingVisitorSelectionForm: FC = () => {
    const navigation = useNavigate();
    const { state: navState } = useLocation();
    const { id } = useParams<{ id: string }>();
    const flow = useGuardingFlow();
    const dispatch = useDispatch<AppDispatch>();
    const { stepData, visitRequest } = useSelector((state: CombinedStates) => state.guardingRequestForm);
    const isRetail = stepData?.isRetail || false;
    const [addedVisitors, setAddedVisitors] = useState<AddedVisitor[]>([]);
    const [showAddManually, setShowAddManually] = useState<boolean>(false);
    const amending = navState?.amending;

    useFormCleanup({
        requestId: (!flow.guestMode && !amending) ? visitRequest.encryptedId : null,
        formBasePath: '/guarding-request/',
        deleteEndpoint: 'guarding-request',
        dispatchDelete: () => {
            if (visitRequest.encryptedId) dispatch(deleteGuardingRequest(visitRequest.encryptedId));
        },
    });

    const handleBackClick = (navigationState: Record<string, unknown>) => {
        flow.goToStep(2, navigationState);
    };

    const handleInitialLoad = async () => {
        if (id !== undefined || flow.guestMode) {
            const resultAction = await flow.loadStep(3, amending);

            if (resultAction.meta.requestStatus === 'rejected') {
                // A guest has no dashboard and no way to create a request: send
                // them back to the link itself, which re-validates the token and
                // shows the invalid/expired message when that is the reason.
                if (flow.guestMode) {
                    navigation(`/booking/${flow.token}`, { replace: true });
                    return;
                }

                navigation('/dashboard', { replace: true });
                notification.error({
                    message: 'Guarding Request Not Found',
                    description: 'The guarding request could not be found. Please create a new request.',
                });
            }
        } else {
            navigation('/dashboard', { replace: true });
            notification.error({
                message: 'Invalid Guarding Request',
                description: 'Please select a guarding request to proceed',
            });
        }
    };

    useEffect(() => {
        handleInitialLoad();
    }, []);

    useEffect(() => {
        if (visitRequest.visitors) {
            setAddedVisitors(visitRequest.visitors);
        }
    }, [visitRequest.visitors]);

    let visitorsSearchList: ClickListData[] = [];

    if (stepData.visitors !== undefined) {
        visitorsSearchList = stepData.visitors.map((visitor) => ({
            value: visitor.id,
            leftString: visitor.name,
            rightString: visitor.email,
        })).sort((a, b) => a.leftString.toLowerCase().localeCompare(b.leftString.toLowerCase()));
    }

    const handleNextClick = (navigationState: Record<string, unknown>) => {
        if (isEmpty(addedVisitors)) {
            notification.error({
                message: 'Visitor Not Selected',
                description: 'Please select a visitor to proceed',
            });
            return;
        }

        if (id !== undefined || flow.guestMode) {
            flow.updateStep(3, {
                visitors: addedVisitors,
                amending,
                forcedStep: 3,
            }).then(unwrapResult).then((payload: VisitRequestFormResponsePayload) => {
                if (payload.stepData) {
                    // Retail: Job Details comes after Visitors (/step/1); non-retail: Review (/step/4).
                    flow.goToStep(isRetail ? 1 : 4, navigationState);
                }
            }).catch(reportUnexpectedRejection);
        }
    };

    const handleSearchVisitorSelect = (value: string | number) => {
        // ClickListData.value is typed string | number; visitor.id is numeric, so
        // normalize before the strict comparison to avoid a string/number mismatch.
        const normalizedValue = typeof value === 'string' ? Number(value) : value;
        const foundVisitor = stepData.visitors?.find((visitor) => visitor.id === normalizedValue);
        if (foundVisitor) {
            setAddedVisitors((prev) => {
                if (prev.some(visitor => visitor.visitorId === foundVisitor.id)) {
                    return prev;
                }
                const updated = [...prev,
                    {
                        id: foundVisitor.id,
                        name: foundVisitor.name,
                        email: foundVisitor.email,
                        visitorId: foundVisitor.id,
                        companyName: foundVisitor.companyName || '',
                        phoneNumber: foundVisitor.phoneNumber || '',
                    },
                ];
                dispatch(updateVisitors(updated));
                return updated;
            });
        }
    };

    const handleRemoveVisitor = (visitor: AddedVisitor) => {
        setAddedVisitors((prev) => {
            const updated = prev.filter((v) => v.id !== visitor.id);
            dispatch(updateVisitors(updated));
            return updated;
        });
    };

    const handleEnterManuallyClick = () => {
        setShowAddManually(true);
    };

    const handleAddVisitorManually = (values: AddVisitorValues) => {
        const newVisitor: AddedVisitor = {
            name: values.fullName,
            email: values.email,
            companyName: values.companyName,
            phoneNumber: values.phoneNumber,
            isTemporary: !values.saveVisitor || false,
        };
        setAddedVisitors((prev) => {
            const updated = [...prev, newVisitor];
            dispatch(updateVisitors(updated));
            return updated;
        });
        setShowAddManually(false);
    };

    const addedVisitorsIds: number[] = [];
    if (addedVisitors.length > 0) {
        addedVisitors.forEach((visitor) => {
            if (visitor.visitorId) {
                addedVisitorsIds.push(visitor.visitorId);
            }
        });
    }

    return (
        <RequestFormLayout
            currentStep={isRetail ? 2 : 3}
            isNextEnabled
            isBackEnabled
            onBackClick={handleBackClick}
            onNextClick={handleNextClick}
            stepperLabels={isRetail ? GUARDING_STEPPER_LABELS_RETAIL : GUARDING_STEPPER_LABELS}
            formTitle="Guarding Request Form"
            reduxSliceKey="guardingRequestForm">
            <>
                <SearchVisitors
                    savedVisitors={visitorsSearchList}
                    onSelect={handleSearchVisitorSelect}
                    onClickEnterManually={handleEnterManuallyClick}
                    selectedVisitors={addedVisitorsIds} />
                {showAddManually && <ManuallyAddVisitor addVisitorSubmit={handleAddVisitorManually} />}
                {addedVisitors.length > 0 && (
                    <VisitorsAdded
                        addedVisitors={addedVisitors}
                        removeVisitor={handleRemoveVisitor} />
                )}
            </>
        </RequestFormLayout>
    );
};

export default GuardingVisitorSelectionForm;
